import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { Col, Row } from 'reactstrap';
import { getTicket, clearTicket } from '../../../../actions/index';
import Loader from '../../../common/Loader';
import ResourceProperty from '../../../common/resource/ResourceProperty';
import TicketVoid from './TicketVoid';
import TicketToolbar from './ticket-toolbar/TicketToolbar';
import { TICKET_PATH } from '../../../../config/paths';
import CreditNote from './CreditNote';
import { TICKET_STATUS } from '../../../../config/constants';
import Refund from './Refund';
import History from './History';
import {
  matchPropTypes,
  breadcrumbsPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';
import Content from '../../../layout/Content';
import NoDataResource from '../../../common/resource/NoDataResource';
import { tzNormalizeDate } from '../../../../utils/date';
import { DATE_TIME_FORMAT } from '../../../../config/locale';
import { numberFormatter } from '../../../../utils/number';
import EnumStatus from '../../../common/EnumStatus';
import TicketChangeHistoryComponent from './TicketChangeHistoryComponent';

const Ticket = ({
  breadcrumbs,
  dispatchGetTicket,
  dispatchClearTicket,
  ticket,
  match: {
    params: { id: ticketId },
  },
  activity,
  isForMine,
}) => {
  useEffect(() => {
    dispatchGetTicket({ ticketId });
    return () => {
      dispatchClearTicket();
    };
  }, [dispatchGetTicket, dispatchClearTicket, ticketId]);

  const getTransaction = (transactionList) => {
    if (transactionList.length === 1) {
      return transactionList[0];
    }

    for (let i = transactionList.length - 1; i >= 0; i -= 1) {
      if (!transactionList[i].creditNoteId) {
        return transactionList[i];
      }
    }

    return transactionList.reduce((max, current) =>
      current.amount > max.amount ? current : max,
    );
  };

  const getUniquePaymentMethods = useCallback(
    (transactions) => {
      const paymentMethodsSet = new Set();
      const { manualPaymentMethod } = ticket.toJS();
      const originalTransaction = getTransaction(transactions);
      const replacedMethodId =
        manualPaymentMethod &&
        manualPaymentMethod.id !== originalTransaction.paymentMethod.id
          ? originalTransaction.paymentMethod.id
          : null;

      transactions.forEach((transaction) => {
        const method = transaction.paymentMethod;
        if (!method) return;

        if (replacedMethodId && method.id === replacedMethodId) return;

        paymentMethodsSet.add(method.name);
      });

      if (manualPaymentMethod) {
        paymentMethodsSet.add(manualPaymentMethod.name);
      }

      return [...paymentMethodsSet].join(', ');
    },
    [ticket],
  );

  let title = 'Boleto';
  let content;
  let toolbar = null;

  if (activity) {
    content = <Loader />;
  } else if (ticket.isEmpty()) {
    content = <NoDataResource returnPage={TICKET_PATH} />;
  } else {
    const ticketJson = ticket.toJS();

    const {
      creditNote,
      ticketStatus: status,
      sourceLocation,
      destinationLocation,
      departureDate,
      seatReservation,
      nextTickets,
      previousTickets,
      customer,
      listPrice,
      ticketVoid,
      user,
      transactions,
      fullDocumentCode,
      documentAssignmentDate,
      contingencyMotive,
    } = ticketJson;

    const {
      voucherType: { id: voucherTypeId },
      business,
      voucherType,
    } = transactions[0];

    const showHistory =
      (nextTickets && nextTickets.length > 0) ||
      (previousTickets && previousTickets.length > 0);

    const tripData = (
      <>
        <h3>Viaje</h3>
        <ResourceProperty label="Origen:">
          {sourceLocation.name}
        </ResourceProperty>
        <ResourceProperty label="Destino:">
          {destinationLocation.name}
        </ResourceProperty>
        <ResourceProperty label="Embarque:">
          {`${sourceLocation.name} - ${sourceLocation.address}`}
        </ResourceProperty>
        <ResourceProperty label="Desembarque:">
          {`${destinationLocation.name} - ${destinationLocation.address}`}
        </ResourceProperty>
        <ResourceProperty label="Fecha de Salida:">
          {departureDate
            ? tzNormalizeDate({
                date: departureDate,
                format: DATE_TIME_FORMAT,
              })
            : '-'}
        </ResourceProperty>
        <ResourceProperty label="Asiento:">
          {seatReservation ? seatReservation.seatNumber : '-'}
        </ResourceProperty>
        <ResourceProperty label="Piso:">
          {seatReservation ? seatReservation.floorNumber : '-'}
        </ResourceProperty>
      </>
    );

    const passengerData = (
      <>
        <h3>Pasajero</h3>
        <ResourceProperty label="Nombre Completo:">
          {customer.fullName}
        </ResourceProperty>
        <ResourceProperty label="Documento de identificación:">
          {`${customer.identificationType.name} ${customer.idDocumentNumber}`}
        </ResourceProperty>
        <ResourceProperty label="País:">
          {customer.idCountryOfOrigin}
        </ResourceProperty>
        <ResourceProperty label="Celular:">
          {customer.mobilePhone || '-'}
        </ResourceProperty>
        <ResourceProperty label="Email:">
          {customer.email || '-'}
        </ResourceProperty>
      </>
    );

    const paymentData = !isForMine && (
      <>
        <h3>Pago</h3>
        <ResourceProperty label="Método de Pago:">
          {getUniquePaymentMethods(transactions)}
        </ResourceProperty>
        <ResourceProperty label="Precio de Lista:">
          {listPrice && numberFormatter({ value: listPrice })}
        </ResourceProperty>
        <ResourceProperty label="Precio de Venta:">
          {numberFormatter({ value: ticketJson.salePrice })}
        </ResourceProperty>
      </>
    );

    const businessData = business && (
      <>
        <h3>Factura</h3>
        <ResourceProperty label="RUC:">
          {business.businessTaxId}
        </ResourceProperty>
        <ResourceProperty label="Razón Social:">
          {business.name}
        </ResourceProperty>
      </>
    );

    const userData = (
      <>
        <h3>Usuario</h3>
        <ResourceProperty label="Nombre Completo:">
          {user.customer.fullName}
        </ResourceProperty>
      </>
    );

    const contingencyData =
      contingencyMotive !== null ? (
        <>
          <h3>Información de Contingencia</h3>
          <ResourceProperty label="Motivo de Contingencia:">
            {contingencyMotive}
          </ResourceProperty>
        </>
      ) : null;

    const ticketVoidComponent = ticketVoid && (
      <TicketVoid ticketVoid={ticketVoid} />
    );

    const creditNoteComponent = creditNote && (
      <CreditNote creditNote={creditNote} />
    );

    const refundComponent = status === TICKET_STATUS.REFUNDED.value && (
      <Refund />
    );

    const ticketChangeHistoryComponent = (
      <>
        <TicketChangeHistoryComponent ticket={ticket.toJS()} />
      </>
    );

    content = (
      <>
        <Row>
          <Col lg={6} md={12}>
            <ResourceProperty label="Tipo de Comprobante:">
              {voucherType.name}
            </ResourceProperty>
            <ResourceProperty label="Fecha de Emisión:">
              {documentAssignmentDate
                ? tzNormalizeDate({
                    date: documentAssignmentDate,
                    format: DATE_TIME_FORMAT,
                  })
                : null}
            </ResourceProperty>
            <ResourceProperty label="Estado:">
              <EnumStatus enumObject={TICKET_STATUS} enumValue={status} />
            </ResourceProperty>
            {tripData}
            {passengerData}
            {paymentData}
            {businessData}
            {userData}
            {contingencyData}
          </Col>
          <Col lg={6} md={12}>
            {showHistory && (
              <History
                nextTickets={nextTickets}
                previousTickets={previousTickets}
              />
            )}
            {ticketVoidComponent}
            {creditNoteComponent}
            {refundComponent}
          </Col>
        </Row>
        {ticketChangeHistoryComponent}
      </>
    );

    toolbar = (
      <TicketToolbar
        creditNote={creditNote || null}
        ticketId={+ticketId}
        ticketStatus={status}
        voucherTypeId={voucherTypeId}
        departureDate={departureDate}
      />
    );

    title = `Boleto ${fullDocumentCode}`;
  }

  return (
    <Content
      breadcrumbs={breadcrumbs}
      toolbar={toolbar}
      title={title}
      content={content}
    />
  );
};

Ticket.propTypes = {
  breadcrumbs: breadcrumbsPropTypes,
  dispatchGetTicket: PropTypes.func.isRequired,
  dispatchClearTicket: PropTypes.func.isRequired,
  ticket: PropTypes.instanceOf(Immutable.Map).isRequired,
  match: matchPropTypes.isRequired,
  activity: PropTypes.bool.isRequired,
  isForMine: PropTypes.bool.isRequired,
};

Ticket.defaultProps = {
  breadcrumbs: [],
};

const mapStateToProps = ({ ReservationUnit, authentication }) => ({
  breadcrumbs: [
    ...ReservationUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Boletos',
      href: TICKET_PATH,
    },
    {
      text: 'Ver',
      href: '',
    },
  ],
  ticket: ReservationUnit.Ticket.getIn(['current', 'content']),
  activity: !ReservationUnit.Ticket.getIn(['current', 'activity']).isEmpty(),
  isForMine: !!authentication.get('user').salesSessionUserId,
});

const mapDispatchToProps = {
  dispatchGetTicket: getTicket,
  dispatchClearTicket: clearTicket,
};

export default connect(mapStateToProps, mapDispatchToProps)(Ticket);
